import { useContext, useState, useEffect } from 'react';
import { Col, Divider, Row, Typography } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table/Table';
import { ApplicationInfomationTable } from '../ApplicationInfomationTable';
import { BaseTable } from 'components/modules/BaseTable';
import { useApplicationViewData } from '../../utils/useApplicationViewData';
import { useAppCategoryResult } from '../../utils/useAppCategoryResult';
import { BreadCrumbContext, SearchContext } from '../index';
import ScoreViewOption, { INIT_FILTER_OPTIONS } from '../common/ScoreViewOption';
import { createColumns } from './createColumns';


// antd の Tree data の表示を使うための型
type DataSource = AppStatusModal.ApplicationView['users'][number];

type Props = {
    category: App.AppsCategory,
    onSearch: ReturnType<typeof useAppCategoryResult>[1],
};

export const ApplicationView: React.FC<Props> = ({
    category,
    onSearch
}) => {
    const { breadcrumb, setBreadcrumb } = useContext(BreadCrumbContext);
    const { search, } = useContext(SearchContext);
    const [applicationView] = useApplicationViewData(breadcrumb.uuid);
    const application = getApplication(category, breadcrumb);
    const [filterOptions, setFilterOptions] = useState<string[]>(INIT_FILTER_OPTIONS);
    const [filterLoading, setFilterLoading] = useState(false);

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        ...applicationView.pagination,
        showTotal: (total, range) => {
            return (`全${total}件中 ${range[0]}-${range[1]} 件`);
        },
        onChange: (page, pageSize) => {
            setPagination(pagination => ({
                ...pagination,
                current: page,
                pageSize,
            }));
            onSearch(category.uuid, search, page, pageSize);
        }
    });

    useEffect(() => {
        onSearch(category.uuid, search, 1, pagination.pageSize ?? 50);
        // 初回のみ実行
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPagination(pagination => ({
            ...pagination,
            total: applicationView.pagination.total,
        }));
    }, [applicationView.pagination]);

    useEffect(() => {
        setFilterLoading(false);
    }, [filterLoading])

    const handleShowStudentView = (name: string, uuid: string) => {
        setBreadcrumb({ name: name, uuid: uuid, type: 'Student' });
    };

    const handleShowHistory = (breadcrumb: AppStatusModal.Breadcrumb, uuid: string) => {
        setBreadcrumb({ uuid: uuid, type: 'History' , history: breadcrumb});
    }

    const columns: ColumnsType<DataSource> = createColumns(
        handleShowStudentView,
        handleShowHistory,
        breadcrumb,
        application,
        filterOptions
    );

    const handleChangeViewOption = (value: string) => {
        setFilterLoading(true);
        setFilterOptions((prev) => {
            if (prev?.length > 0 && prev?.includes(value)) {
                return [...prev.filter((item) => item !== value)];
            }
            return [...prev, value];
        });
    };

    return (
        <div>
            <Row gutter={16}>
                <Col xs={24} sm={24} md={24} lg={24} xl={16}>
                    <Typography.Title level={3}>{applicationView.application?.title}</Typography.Title>
                    <Divider style={{ marginTop: '0.5rem', marginBottom: '2rem' }} />

                    <ScoreViewOption onChange={handleChangeViewOption} />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={8}>
                    <ApplicationInfomationTable
                        title={applicationView.application?.title ?? ''}
                        publishStart={applicationView.application?.publish_start_at ?? 0}
                        publishEnd={applicationView.application?.publish_end_at ?? 0}
                        isUsable={!!applicationView.application?.result_url}
                        maxAttempts={applicationView.application?.max_attempts ?? 0}
                    />
                </Col>
            </Row>

            <BaseTable
                rowKey='uuid'
                columns={columns}
                dataSource={applicationView.users}
                emptyDescription={''} // 通常空になることはない
                pagination={pagination}
                loading={applicationView.loading || filterLoading}
                tableLayout='auto'
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

const getApplication = (
    category: App.AppsCategory,
    breadcrumb: AppStatusModal.Breadcrumb
): App.ForAdmin | App.ForManager | undefined => {
    const applications: any[] = category.applications ?? [];
    return applications.find((app) => app.uuid === breadcrumb.uuid);
};